<template>
  <section class="services section">
    <div class="container">
      <h1 class="title mb-0">Services</h1>
      <div class="d-flex justify-content-between flex-wrap mt-3">
        <div
          class="d-flex align-items-center mr-5 text-dark col-12 mb-3 mb-md-0 pl-0"
        >
          <span>
            <b v-if="!loading">
              <i class="fas fa-spin fa-circle-notch" v-if="busy"></i>
              {{ busy ? "" : fetchedServices.length }}/{{ total }}</b
            >
            <i class="fas fa-spin fa-circle-notch" v-if="loading"></i>
            Service{{ loading || total != 1 ? "s" : "" }}
          </span>
        </div>
        <div class="input-group my-3">
          <input
            type="text"
            class="form-control"
            placeholder="Add New Primary Service"
            v-model="services.primary.name"
            v-on:keyup.enter="addPrimaryService"
          />
          <div class="input-group-append">
            <button
              class="btn btn-success"
              @click="addPrimaryService"
              :disabled="services.primary.name == `` ? true : false"
            >
              <span v-if="!saving"><icon type="plus" /> Add Service</span>
              <span
                class="spinner-border spinner-border-sm"
                v-if="saving"
                role="status"
                aria-hidden="true"
              ></span>
            </button>
          </div>
        </div>
      </div>
      <loading v-if="loading" class="white-bg shadow-card" />
      <div class="shadow-card" v-if="fetchedServices.length > 0 && !loading">
        <div class="card-body p-0">
          <table class="table table-striped table-hover mb-0 rounded">
            <thead>
              <tr>
                <th>
                  Name
                  <i
                    class="fa fa-info-circle ml-2 cursor-pointer"
                    v-tooltip="'click service/sub-service name to edit'"
                  ></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="service in fetchedServices" :key="service.id">
                <td>
                  <div class="row">
                    <div class="col">
                      <div class="cursor" v-on:click="editPrimary(service)">
                        <span
                          v-if="service.id != services.primary.editing.id"
                          >{{ service.name }}</span
                        >
                        <div
                          v-if="service.id == services.primary.editing.id"
                          class="clearfix d-flex flex-wrap"
                        >
                          <input
                            type="text"
                            class="form-control form-control-sm w-50 mr-2 my-2 float-left"
                            v-model="services.primary.editing.name"
                            v-on:keyup.enter="updatePrimaryService"
                          />
                          <div class="d-flex my-2">
                            <button
                              class="btn btn-sm btn-outline-primary mr-2"
                              @click="updatePrimaryService"
                            >
                              <span v-if="!saving">Save</span>
                              <span
                                class="spinner-border spinner-border-sm"
                                v-if="saving"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </button>
                            <button
                              class="btn btn-sm btn-danger mr-2"
                              :disabled="deleting"
                              @click="deletePrimaryService"
                            >
                              <span v-if="!deleting">Delete</span>
                              <span
                                class="spinner-border spinner-border-sm"
                                v-if="deleting"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </button>
                            <button
                              class="btn btn-sm btn-secondary"
                              @click="cancelPrimary"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                      <ul
                        v-if="service.sub.length > 0"
                        class="mb-0 small text-secondary"
                      >
                        <li v-for="c in service.sub" :key="c.id">
                          <span
                            class="cursor"
                            v-on:click="editChild($event, c)"
                            >{{ c.name }}</span
                          >
                          <ul>
                            <li v-for="(option, key) in c.options" :key="key">
                              {{ option }}
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                    <div class="col-sm-3 text-right">
                      <button
                        class="btn btn-sm btn-primary"
                        :disabled="service.toggling"
                        v-if="!services.childService.type && !service.private"
                        @click="showAddChildForm(service)"
                      >
                        <icon type="plus" /> Add Sub-Service
                      </button>
                      <h3
                        class="custom-control custom-switch mt-4"
                        v-if="!services.childService.type"
                      >
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          :id="`private_service_${service.id}`"
                          :disabled="service.toggling || service.sub.length > 0"
                          @input="toggleServiceType(service)"
                          v-model="service.private"
                        />
                        <label
                          class="custom-control-label privacy-disabled"
                          :for="`private_service_${service.id}`"
                          v-if="service.sub.length > 0"
                          v-tooltip.top="'Remove sub services to enable'"
                        >
                          <badge
                            :color="
                              service.toggling
                                ? 'info'
                                : service.private
                                ? 'danger'
                                : 'secondary'
                            "
                            v-html="
                              service.toggling
                                ? '<span class=\'spinner-border spinner-border-sm ml-2 mr-2\'></span>'
                                : service.private
                                ? 'Secret'
                                : 'Public'
                            "
                          ></badge>
                        </label>
                        <label
                          class="custom-control-label cursor-pointer"
                          :for="`private_service_${service.id}`"
                          v-else
                        >
                          <badge
                            :color="
                              service.toggling
                                ? 'info'
                                : service.private
                                ? 'danger'
                                : 'secondary'
                            "
                            v-html="
                              service.toggling
                                ? '<span class=\'spinner-border spinner-border-sm ml-2 mr-2\'></span>'
                                : service.private
                                ? 'Secret'
                                : 'Public'
                            "
                          ></badge>
                        </label>
                      </h3>
                    </div>
                  </div>
                  <sub-service-form
                    v-if="
                      services.childService.type &&
                      services.childService.parent_id == service.id
                    "
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <mugen-scroll :handler="loadMore" :should-handle="!busy && !loading">
            <alert v-if="busy" class="text-center p-2 mb-0 rounded-0"
              ><i class="fas fa-spin fa-circle-notch mr-2"></i>Loading more
              services...</alert
            >
          </mugen-scroll>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from "vuex";
import SubServiceForm from "./SubServiceForm";
import Swal from "sweetalert2";
import MugenScroll from "vue-mugen-scroll";

export default {
  name: "services",
  components: {
    SubServiceForm,
    MugenScroll,
  },
  data() {
    return {
      deleting: false,
      limit: 50,
      busy: false,
    };
  },
  computed: {
    ...mapState(["saving", "loading", "services"]),
    ...mapState({
      fetchedServices: (state) => state.services.pagination.data,
      current: (state) => state.services.pagination.current,
      total: (state) => state.services.pagination.total || 0,
    }),
    loading: function () {
      return this.$store.state.loading;
    },
  },
  mounted() {
    this.getservices({
      limit: this.limit,
    });
  },
  methods: {
    ...mapActions({
      getservices: "services/allPaginated",
      updateServicePrivacy: "services/updateServicePrivacy",
    }),
    addPrimaryService: function () {
      let primary = this.services.primary;
      if (primary != "") {
        this.$store.commit("SET_SAVING_STATE", true, { root: true });
        this.$store.dispatch("services/addPrimaryService", primary);
      }
    },
    toggleServiceType: function (service) {
      if (service.sub.length != 0) {
        setTimeout(() => {
          service.private = false;
          this.$forceUpdate();
        }, 0);
        return;
      }
      if (service.toggling) {
        service.private = !service.private;
        return;
      }
      service.toggling = true;
      this.updateServicePrivacy({
        id: service.id,
        private: !service.private,
      }).then((success) => {
        if (!success) {
          service.private = !service.private;
        }
        service.toggling = false;
        this.$forceUpdate();
      });
    },
    updatePrimaryService: function (event) {
      event.stopPropagation();
      this.$store.commit("SET_SAVING_STATE", true, { root: true });
      this.$store.dispatch(
        "services/updatePrimaryService",
        this.services.primary.editing
      );
    },
    deletePrimaryService: function (event) {
      event.stopPropagation();
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });
      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete",
          cancelButtonText: "No, cancel!",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            this.deleting = true;
            this.$store
              .dispatch(
                "services/deletePrimaryService",
                this.services.primary.editing
              )
              .then(() => {
                this.deleting = false;
              });
          }
        });
    },
    cancelPrimary: function (event) {
      event.stopPropagation();
      this.services.primary.editing = { name: "", id: null };
    },
    showAddChildForm(service) {
      this.services.childService.id = null;
      this.services.childService.type = "creating";
      this.services.childService.parent_id = service.id;
    },
    editChild(event, c) {
      event.stopPropagation();
      this.$store.commit("services/CANCEL_EDITING");
      this.services.childService = c;
      this.services.tmp = c.name;
      this.services.childService.type = "editing";
    },
    editPrimary(service) {
      this.services.primary.editing.id = service.id;
      this.services.primary.editing.name = service.name;
    },
    loadMore() {
      if (this.fetchedServices.length < this.total) {
        this.busy = true;
        this.getservices({
          limit: this.limit,
          page: this.current + 1,
        }).then(() => {
          this.busy = false;
        });
      }
    },
  },
};
</script>
<style scoped lang="scss">
.privacy-disabled {
  opacity: 0.5;
}
</style>
